/**
 * Geocoding Service
 * Handles address search and geocoding using Mapbox Geocoding API.
 */

import { Coordinates } from '../types';
import { getApiConfig } from '../config/api';

/**
 * Geocoding result
 */
export interface GeocodingResult {
  /** Place name/address */
  placeName: string;
  /** Coordinates */
  coordinates: Coordinates;
  /** Full address */
  address?: string;
  /** Place type */
  placeType?: string[];
}

/**
 * Geocoding response from Mapbox API
 */
interface MapboxGeocodingResponse {
  features: Array<{
    id: string;
    type: string;
    place_type: string[];
    relevance: number;
    properties: {
      accuracy?: string;
    };
    text: string;
    place_name: string;
    center: [number, number]; // [lng, lat]
    geometry: {
      type: string;
      coordinates: [number, number];
    };
    context?: Array<{
      id: string;
      text: string;
      short_code?: string;
    }>;
  }>;
}

/**
 * Search for addresses using Mapbox Geocoding API.
 * @param query - Address search query
 * @param options - Search options
 * @returns Promise resolving to array of geocoding results
 */
export async function searchAddress(
  query: string,
  options?: {
    limit?: number;
    proximity?: Coordinates;
    types?: string[];
  }
): Promise<GeocodingResult[]> {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const config = getApiConfig();
  const limit = options?.limit || 5;
  const types = options?.types?.join(',') || 'address,poi,place';

  let url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${config.mapboxToken}&limit=${limit}&types=${types}`;

  // Add proximity bias if provided
  if (options?.proximity) {
    url += `&proximity=${options.proximity.lng},${options.proximity.lat}`;
  }

  const startTime = performance.now();
  try {
    const response = await fetch(url);

    if (!response.ok) {
      // Track error in PostHog
      (async () => {
        const { trackEvent } = await import('./posthogService');
        trackEvent('geocoding_api_error', {
          query,
          status: response.status,
          status_text: response.statusText,
        });
      })();
      
      throw new Error(`Geocoding API error: ${response.status} ${response.statusText}`);
    }

    const data: MapboxGeocodingResponse = await response.json();
    
    // Track success in PostHog
    const duration = performance.now() - startTime;
    (async () => {
      const { trackEvent } = await import('./posthogService');
      trackEvent('geocoding_api_success', {
        query,
        results_count: data.features.length,
        duration_ms: Math.round(duration),
      });
    })();

    return data.features.map((feature) => ({
      placeName: feature.text,
      coordinates: {
        lat: feature.center[1],
        lng: feature.center[0],
      },
      address: feature.place_name,
      placeType: feature.place_type,
    }));
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
}

/**
 * Location details from reverse geocoding
 * Includes all levels from country down to street
 */
export interface LocationDetails {
  /** Full address/place name */
  address: string | null;
  /** Country name */
  country: string | null;
  /** Region/state/province */
  region: string | null;
  /** District/administrative area */
  district: string | null;
  /** City name */
  city: string | null;
  /** Neighborhood/borough */
  neighborhood: string | null;
  /** Postal/ZIP code */
  postcode: string | null;
  /** Street name */
  street: string | null;
}

/**
 * Reverse geocode coordinates to get address.
 * @param coordinates - Coordinates to reverse geocode
 * @returns Promise resolving to address string or null
 */
export async function reverseGeocode(
  coordinates: Coordinates
): Promise<string | null> {
  const config = getApiConfig();
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${coordinates.lng},${coordinates.lat}.json?access_token=${config.mapboxToken}&limit=1`;

  const startTime = performance.now();
  try {
    const response = await fetch(url);

    if (!response.ok) {
      // Track error in PostHog
      (async () => {
        const { trackEvent } = await import('./posthogService');
        trackEvent('reverse_geocoding_api_error', {
          coordinates,
          status: response.status,
        });
      })();
      
      throw new Error(`Reverse geocoding API error: ${response.status}`);
    }

    const data: MapboxGeocodingResponse = await response.json();
    
    // Track success in PostHog
    const duration = performance.now() - startTime;
    (async () => {
      const { trackEvent } = await import('./posthogService');
      trackEvent('reverse_geocoding_api_success', {
        coordinates,
        has_result: data.features.length > 0,
        duration_ms: Math.round(duration),
      });
    })();

    if (data.features.length > 0) {
      return data.features[0].place_name;
    }

    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Reverse geocode coordinates to get detailed location information.
 * Extracts all location levels from country down to street from Mapbox reverse geocoding response.
 * @param coordinates - Coordinates to reverse geocode
 * @returns Promise resolving to location details
 */
export async function reverseGeocodeWithDetails(
  coordinates: Coordinates
): Promise<LocationDetails> {
  const config = getApiConfig();
  // Request all location types to get full context
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${coordinates.lng},${coordinates.lat}.json?access_token=${config.mapboxToken}&limit=1&types=country,region,district,place,locality,neighborhood,postcode,address`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Reverse geocoding API error: ${response.status}`);
    }

    const data: MapboxGeocodingResponse = await response.json();

    if (data.features.length === 0) {
      return {
        address: null,
        country: null,
        region: null,
        district: null,
        city: null,
        neighborhood: null,
        postcode: null,
        street: null,
      };
    }

    // Extract location details from context array and feature properties
    const feature = data.features[0];
    const address = feature.place_name || null;

    // Debug logging when VITE_DEBUG is enabled
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[reverseGeocodeWithDetails] Feature:', {
        place_type: feature.place_type,
        text: feature.text,
        place_name: feature.place_name,
        context: feature.context?.map(item => ({ id: item.id, text: item.text })),
      });
    }

    // Parse context array to extract all location levels
    // Mapbox context format: [{ id: 'country.xxx', text: 'Country Name', ... }, ...]
    // Context IDs can be: country, region, postcode, district, place, locality, neighborhood, address
    let country: string | null = null;
    let region: string | null = null;
    let district: string | null = null;
    let city: string | null = null;
    let neighborhood: string | null = null;
    let postcode: string | null = null;
    let street: string | null = null;

    // Check if the feature itself is an address (street level)
    // The feature.text contains the street name when place_type includes 'address'
    if (feature.place_type?.includes('address') && feature.text) {
      street = feature.text;
    }

    // Parse context array - iterate in reverse order to get most specific first
    if (feature.context) {
      // Process context in reverse to prioritize more specific items
      for (let i = feature.context.length - 1; i >= 0; i--) {
        const item = feature.context[i];
        const id = item.id || '';
        const text = item.text || null;
        
        // Mapbox context IDs use format: "type.id" (e.g., "postcode.510699", "district.123")
        // We need to check the type part before the dot
        const typeMatch = id.match(/^([^.]+)\./);
        const type = typeMatch ? typeMatch[1] : '';

        switch (type) {
          case 'country':
            if (!country) country = text;
            break;
          case 'region':
          case 'province':
          case 'state':
            if (!region) region = text;
            break;
          case 'district':
            if (!district) district = text;
            break;
          case 'postcode':
            if (!postcode) postcode = text;
            break;
          case 'place':
          case 'locality':
            // Use the most specific place/locality as city
            if (!city) city = text;
            break;
          case 'neighborhood':
          case 'borough':
            if (!neighborhood) neighborhood = text;
            break;
          case 'address':
            // Street level address from context
            if (!street) street = text;
            break;
        }
      }
    }

    // If we still don't have a street and the feature is an address type, use feature.text
    if (!street && feature.place_type?.includes('address') && feature.text) {
      street = feature.text;
    }

    // Last resort: try to extract street from address string
    // But be careful - don't extract postcodes or other non-street data
    if (address && !street) {
      const parts = address.split(',');
      // The first part is usually the street, but check if it looks like a postcode
      // Postcodes are typically numeric or alphanumeric patterns
      if (parts.length > 0) {
        const firstPart = parts[0].trim();
        // Don't use if it looks like a postcode (all digits or common postcode patterns)
        const isPostcode = /^\d+$/.test(firstPart) || /^\d{4,6}$/.test(firstPart);
        if (!isPostcode && firstPart.length > 0) {
          street = firstPart;
        }
      }
    }

    const result = {
      address,
      country,
      region,
      district,
      city,
      neighborhood,
      postcode,
      street,
    };

    // Debug logging when VITE_DEBUG is enabled
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[reverseGeocodeWithDetails] Extracted location details:', result);
    }

    return result;
  } catch (error) {
    console.error('Reverse geocoding with details error:', error);
    return {
      address: null,
      country: null,
      region: null,
      district: null,
      city: null,
      neighborhood: null,
      postcode: null,
      street: null,
    };
  }
}

